import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [resetLink, setResetLink] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/forgot-password`,
        { email }
      );
      
      // Show the reset link (in production, this would be sent via email)
      if (response.data.reset_url) {
        setResetLink(response.data.reset_url);
        toast.success('Password reset link generated!');
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Google sign-in')) {
        toast.error('This account uses Google sign-in. Please use "Sign in with Google" button.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to process request');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyResetLink = () => {
    navigator.clipboard.writeText(resetLink);
    toast.success('Reset link copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f0f7ff] to-white flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2563eb] to-[#1e40af] flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-white" fill="currentColor" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Forgot Password</h1>
          <p className="text-gray-600">Enter your email to reset your password</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
          {!resetLink ? (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="email-input"
                      required
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-button"
                  className="w-full bg-[#2563eb] hover:bg-[#1e40af] text-white py-3 rounded-lg font-semibold disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Send Reset Link'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="text-[#2563eb] hover:underline font-semibold inline-flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Sign In
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Reset Link Generated</h3>
                <p className="text-sm text-gray-700 mb-3">
                  In production, this link would be sent to your email. For now, use this link to reset your password:
                </p>
                <div className="bg-white p-3 rounded border border-gray-300 break-all text-sm text-gray-700 mb-3">
                  {resetLink}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={copyResetLink}
                    data-testid="copy-link-button"
                    variant="outline"
                    className="flex-1"
                  >
                    Copy Link
                  </Button>
                  <Button
                    onClick={() => window.location.href = resetLink}
                    data-testid="use-link-button"
                    className="flex-1 bg-[#2563eb] hover:bg-[#1e40af] text-white"
                  >
                    Use Link
                  </Button>
                </div>
              </div>

              <div className="text-center">
                <Link to="/login" className="text-[#2563eb] hover:underline font-semibold inline-flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Sign In
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
